import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST } from '../../../../constants/common'
import { PdfViewerEvent } from '../../PdfViewer.constants'

import {
  checkIsPdfViewerFindPending,
  getPdfViewerMatchedPageIndexes,
  getPdfViewerSearchResultId,
  getPdfViewerSearchResults,
  loadPdfViewerPageTexts,
} from './usePdfViewerSearch.utils'

export function usePdfViewerSearchResults({
  eventBusRef,
  findControllerRef,
  pdfDocument,
}) {
  const [matches, setMatches] = useState(null)
  const [results, setResults] = useState(EMPTY_LIST)
  const [selectedResultId, setSelectedResultId] = useState(null)
  const [isLoading, setLoading] = useState(false)

  const resultsRequestRef = useRef(0)

  // a page read still running for an abandoned query must not land its results
  function invalidateResults() {
    resultsRequestRef.current += 1
  }

  function resetResults() {
    invalidateResults()

    setMatches(null)
    setResults(EMPTY_LIST)
    setSelectedResultId(null)
    setLoading(false)
  }

  function startLoading() {
    setLoading(true)
  }

  useEffect(() => {
    const eventBus = eventBusRef.current

    if (eventBus && pdfDocument) {
      const abortController = new AbortController()
      const { signal } = abortController
      const pageTextPromises = new Map()

      // the found state arrives with the first match while the remaining pages
      // keep reporting counts, each report superseding the pending page read —
      // so the loader follows the latest known state on whichever read lands
      let findState = null

      async function updateResults() {
        const requestId = resultsRequestRef.current + 1

        resultsRequestRef.current = requestId

        const findController = findControllerRef.current

        const pageTexts = await loadPdfViewerPageTexts({
          pageIndexes: getPdfViewerMatchedPageIndexes(findController),
          pageTextPromises,
          pdfDocument,
        })

        if (resultsRequestRef.current === requestId) {
          setResults(getPdfViewerSearchResults({ findController, pageTexts }))
          setLoading(checkIsPdfViewerFindPending(findState))
        }
      }

      function handleUpdateMatches({ matchesCount, state }) {
        const { matchIdx, pageIdx } = findControllerRef.current.selected

        if (state !== undefined) {
          findState = state
        }

        setMatches(matchesCount)
        setSelectedResultId(
          getPdfViewerSearchResultId({
            matchIndex: matchIdx,
            pageIndex: pageIdx,
          })
        )
        updateResults()
      }

      eventBus.on(PdfViewerEvent.UpdateFindMatchesCount, handleUpdateMatches, {
        signal,
      })
      eventBus.on(PdfViewerEvent.UpdateFindControlState, handleUpdateMatches, {
        signal,
      })

      return () => {
        abortController.abort()
        resetResults()
      }
    }
  }, [pdfDocument])

  return {
    invalidateResults,
    resetResults,
    resultsProps: {
      isLoading,
      matches,
      results,
      selectedResultId,
    },
    startLoading,
  }
}
